import type { A2uiMessage, SurfaceModel } from "@a2ui/web_core/v0_9";
import { MessageProcessor } from "@a2ui/web_core/v0_9";
import type {
  A2uiOperation,
  AnalysisReportArtifact,
  AnalysisReportDelivery,
  AnalysisReportLifecycle
} from "@lokasi-ai-web-sdk/agent-ui";

import { reportCatalog } from "./catalog";
import { changedBlockIds } from "./changedBlocks";
import { REPORT_CATALOG_ID, REPORT_ROOT_ID } from "./contract";

export type ReportSurfaceStatus = "idle" | "writing" | "stored";

/** What the panel reads, one immutable object per real change. */
export interface ReportViewState {
  readonly activeSurfaceId: string | null;
  /**
   * A second report the run has announced but not yet filled. The panel keeps
   * showing the active one until this one has content to take its place.
   */
  readonly incomingSurfaceId: string | null;
  /**
   * The shown report is a stored one that new frames are writing over. What is
   * on screen is the old version until the run publishes.
   */
  readonly isRewriting: boolean;
  /**
   * Blocks the shown version rewrote against the one before it. Empty for a
   * first version, while a newer one is being written, and on a replay.
   */
  readonly changedBlockIds: readonly string[];
  readonly status: ReportSurfaceStatus;
  readonly version: number | null;
  readonly title: string | null;
  readonly createdAt: string | null;
  readonly provisional: boolean;
  /** Set while the panel shows a past version instead of the live surface. */
  readonly previewVersion: number | null;
  readonly artifacts: readonly AnalysisReportArtifact[];
}

/**
 * The host's surface store for analysis reports.
 *
 * It owns the one `MessageProcessor` of the thread and applies each delivery's
 * operations by reference, in arrival order: it never sorts, merges, or folds
 * them. The protocol has no delete, so a section leaves the screen only by
 * becoming unreachable from `root`.
 */
export interface ReportSurfaceStore {
  /** Applies one delivery. Replayed operations are dropped, not re-applied. */
  applyDelivery(delivery: AnalysisReportDelivery): void;
  /** One terminal per run; decides whether the visible draft is provisional. */
  applyLifecycle(event: AnalysisReportLifecycle): void;
  /** Indexes one artifact for the transcript card. */
  noteArtifact(artifact: AnalysisReportArtifact): void;
  /**
   * Shows one report in the panel, e.g. from an artifact card. Without a
   * `toolCallId`, or with the call that published the surface's newest
   * version, that is the live surface; an earlier publish call replays its own
   * version beside it, leaving the live one untouched.
   */
  showVersion(surfaceId: string, toolCallId?: string): void;
  getSurface(surfaceId: string): SurfaceModel | undefined;
  subscribe(listener: () => void): () => void;
  getSnapshot(): ReportViewState;
  /** Drops the whole thread: the old processor is disposed, not reused. */
  reset(): void;
}

/** Everything the header shows about one surface, kept per surface. */
interface SurfaceRecord {
  status: ReportSurfaceStatus;
  version: number | null;
  title: string | null;
  createdAt: string | null;
  /** The publish call holding this surface's newest version. */
  latestToolCallId: string | null;
  changedBlockIds: readonly string[];
}

/** One stored version, complete enough to render on its own. */
interface PublishedVersion {
  toolCallId: string;
  surfaceId: string;
  version: number;
  operations: readonly A2uiOperation[];
}

const IDLE: ReportViewState = {
  activeSurfaceId: null,
  incomingSurfaceId: null,
  isRewriting: false,
  changedBlockIds: [],
  status: "idle",
  version: null,
  title: null,
  createdAt: null,
  provisional: false,
  previewVersion: null,
  artifacts: []
};

/**
 * A surface has something to draw once a block has arrived. A `root` alone
 * draws nothing: it only lists children that may not have come yet.
 */
export function surfaceHasContent(surface: SurfaceModel | undefined) {
  if (!surface) return false;
  for (const [id] of surface.componentsModel.entries) {
    if (id !== REPORT_ROOT_ID) return true;
  }
  return false;
}

function createProcessor() {
  // `createSurface` carries the catalog id and the processor refuses a surface
  // whose catalog it does not hold. One store applies every operation the
  // session hands over, which is what the SDK's one-receiver rule expects.
  return new MessageProcessor([reportCatalog], undefined, {
    version: "v0.9"
  });
}

export function createReportSurfaceStore(): ReportSurfaceStore {
  let processor = createProcessor();
  let activeSurfaceId: string | null = null;
  let incomingSurfaceId: string | null = null;
  let artifacts: readonly AnalysisReportArtifact[] = [];
  let snapshot: ReportViewState = IDLE;

  const listeners = new Set<() => void>();
  /** Header metadata per surface, so switching reports keeps each one's own. */
  const records = new Map<string, SurfaceRecord>();
  /** Surfaces that took progressive frames in the run now in flight. */
  const touched = new Set<string>();
  /** Surfaces whose visible content is not what the backend stored. */
  const provisional = new Set<string>();
  /**
   * Every version published in this thread, keyed by the call that published
   * it. Each payload is complete on its own, which is what makes a replay a
   * plain re-apply. They are tens of KB apiece and go with `reset()`; versions
   * published before this session never arrive here at all.
   */
  const published = new Map<string, PublishedVersion>();

  /**
   * A past version on screen. It gets its own processor, because a surface id
   * may exist once per model and the live one already holds it.
   */
  let preview:
    | (PublishedVersion & { processor: ReturnType<typeof createProcessor> })
    | null = null;

  const closePreview = () => {
    if (!preview) return false;
    preview.processor.model.dispose();
    preview = null;
    return true;
  };

  const recordFor = (surfaceId: string): SurfaceRecord => {
    let record = records.get(surfaceId);
    if (!record) {
      record = {
        status: "idle",
        version: null,
        title: null,
        createdAt: null,
        latestToolCallId: null,
        changedBlockIds: []
      };
      records.set(surfaceId, record);
    }
    return record;
  };

  const emit = () => {
    const visible = activeSurfaceId ? records.get(activeSurfaceId) : undefined;
    const isProvisional =
      preview === null &&
      activeSurfaceId !== null &&
      provisional.has(activeSurfaceId);
    snapshot = {
      activeSurfaceId,
      incomingSurfaceId,
      // A report with a stored version, being written over. A run that ended
      // without publishing is not writing any more.
      isRewriting:
        preview === null &&
        visible?.status === "writing" &&
        visible.version !== null &&
        !isProvisional,
      changedBlockIds: preview ? [] : (visible?.changedBlockIds ?? []),
      // A replayed version was published, so it never inherits the live
      // surface's "writing" status or its provisional badge.
      status: preview ? "stored" : (visible?.status ?? "idle"),
      version: preview ? preview.version : (visible?.version ?? null),
      title: visible?.title ?? null,
      createdAt: visible?.createdAt ?? null,
      provisional: isProvisional,
      previewVersion: preview?.version ?? null,
      artifacts
    };
    listeners.forEach((listener) => listener());
  };

  const isReportSurface = (surfaceId: string) =>
    processor.model.getSurface(surfaceId)?.catalog.id === REPORT_CATALOG_ID;

  const hasContent = (surfaceId: string) =>
    surfaceHasContent(processor.model.getSurface(surfaceId));

  /**
   * The publish lane replays a surface's whole history, `createSurface`
   * included, and the processor throws when the surface already exists. Drop
   * the replay so a publish applies only what the progressive lane missed.
   */
  const isReplayedCreateSurface = (operation: A2uiOperation) => {
    const payload = operation.createSurface;
    if (typeof payload !== "object" || payload === null) return false;
    const { surfaceId } = payload as { surfaceId?: unknown };
    return (
      typeof surfaceId === "string" &&
      processor.model.getSurface(surfaceId) !== undefined
    );
  };

  /**
   * What a card is asking for. A call this thread never published resolves to
   * the live surface rather than to nothing: the card cannot name a version it
   * has no metadata for either, so the newest is the honest answer.
   */
  const resolve = (
    surfaceId: string,
    toolCallId?: string
  ): "live" | PublishedVersion | null => {
    const live = processor.model.getSurface(surfaceId) ? "live" : null;
    const stored = toolCallId ? published.get(toolCallId) : undefined;
    if (!stored || stored.surfaceId !== surfaceId) return live;

    // The live surface stands for this version only while it is the published
    // head with nothing being written over it. Mid-rewrite it is a newer,
    // half-finished version, so the stored copy is what the card asked for.
    const record = records.get(surfaceId);
    return record?.status === "stored" && record.latestToolCallId === toolCallId
      ? live
      : stored;
  };

  /** Decides which report the panel shows once a delivery has been applied. */
  const settleActiveSurface = (delivery: AnalysisReportDelivery) => {
    const { surfaceId } = delivery.report;

    if (isReportSurface(surfaceId)) {
      if (activeSurfaceId === null) {
        activeSurfaceId = surfaceId;
      } else if (
        delivery.source === "progressive" &&
        surfaceId !== activeSurfaceId
      ) {
        // Another report is being written. Only a publish or a card moves the
        // panel on its own; a live one waits until it has content.
        incomingSurfaceId = surfaceId;
      }
    }

    if (incomingSurfaceId !== null && hasContent(incomingSurfaceId)) {
      if (incomingSurfaceId !== activeSurfaceId) {
        closePreview();
        activeSurfaceId = incomingSurfaceId;
      }
      incomingSurfaceId = null;
    }
  };

  // Kept whole, from before `applyDelivery` strips the replayed
  // `createSurface`: a replay starts from a processor holding no surface at
  // all, so it needs the operations exactly as the backend folded them.
  // Only a payload the live model accepted is worth replaying later.
  const rememberPublished = (delivery: AnalysisReportDelivery) => {
    const { toolCallId } = delivery;
    const { surfaceId, version } = delivery.report;
    if (
      delivery.source !== "publish" ||
      toolCallId === undefined ||
      typeof version !== "number"
    ) {
      return;
    }

    // Against the version this one replaces. A publish seen twice is not a
    // new version, and a first one has nothing to differ from.
    const newest = records.get(surfaceId)?.latestToolCallId;
    const earlier =
      newest && newest !== toolCallId ? published.get(newest) : undefined;
    if (earlier) {
      recordFor(surfaceId).changedBlockIds = changedBlockIds(
        earlier.operations,
        delivery.operations
      );
    }

    published.set(toolCallId, {
      toolCallId,
      surfaceId,
      version,
      operations: delivery.operations
    });
  };

  const applyDelivery = (delivery: AnalysisReportDelivery) => {
    const { surfaceId } = delivery.report;
    const operations = delivery.operations.filter(
      (operation) => !isReplayedCreateSurface(operation)
    );

    try {
      if (operations.length > 0) {
        // Operations are opaque by design — the backend already validated them
        // against the catalog — so this cast is the one place they become
        // protocol messages.
        processor.processMessages([...operations] as unknown as A2uiMessage[]);
      }
    } catch {
      // A frame the model refuses leaves the last good surface on screen, with
      // no state change (AR9). The SDK swallows the throw too; catching here
      // is what keeps the store consistent with what is rendered.
      return;
    }

    settleActiveSurface(delivery);
    rememberPublished(delivery);

    const record = recordFor(surfaceId);
    if (delivery.source === "progressive") {
      touched.add(surfaceId);
      record.status = "writing";
      record.changedBlockIds = [];
    } else {
      // Publish and restore both carry a stored version; restore is inert
      // until threads are reloadable (Phase 7), but applying it is the same
      // one line and cannot hurt.
      provisional.delete(surfaceId);
      record.status = "stored";
      record.version = delivery.report.version ?? record.version;
      record.title = delivery.report.title ?? record.title;
      record.createdAt = delivery.report.createdAt ?? record.createdAt;
      if (delivery.toolCallId !== undefined) {
        record.latestToolCallId = delivery.toolCallId;
      }
    }

    // A newly published version moves the panel back to what is current. Only
    // a publish does this: progressive frames arrive several times a second
    // while a report is rewritten, and evicting on those would make a replay
    // impossible to keep on screen.
    if (delivery.source !== "progressive" && preview?.surfaceId === surfaceId) {
      closePreview();
    }

    emit();
  };

  const applyLifecycle = (event: AnalysisReportLifecycle) => {
    if (event.type === "run-started") {
      touched.clear();
      return;
    }

    // A surface touched this run but missing from the run's publish list is
    // on screen and nowhere else. Surface id and artifact id are the same
    // value by contract.
    const publishedIds = new Set(event.publishedArtifactIds);
    let changed = false;
    for (const surfaceId of touched) {
      if (!publishedIds.has(surfaceId) && !provisional.has(surfaceId)) {
        provisional.add(surfaceId);
        changed = true;
      }
    }

    // The run is over, so a report that never got content is not coming.
    if (incomingSurfaceId !== null) {
      incomingSurfaceId = null;
      changed = true;
    }

    if (changed) emit();
  };

  const noteArtifact = (artifact: AnalysisReportArtifact) => {
    // The SDK already claims each publish once; keying on the tool call keeps
    // one card per version while a re-sent index adds nothing.
    if (artifacts.some((known) => known.toolCallId === artifact.toolCallId)) {
      return;
    }
    artifacts = [...artifacts, artifact];
    emit();
  };

  const showVersion = (surfaceId: string, toolCallId?: string) => {
    const target = resolve(surfaceId, toolCallId);
    if (target === null) return;

    if (target === "live") {
      const closed = closePreview();
      const moved = activeSurfaceId !== surfaceId;
      activeSurfaceId = surfaceId;
      if (closed || moved) emit();
      return;
    }

    if (preview?.toolCallId === target.toolCallId) return;

    const closed = closePreview();
    const replay = createProcessor();

    try {
      replay.processMessages([
        ...target.operations
      ] as unknown as A2uiMessage[]);
    } catch {
      // A version this build can no longer render leaves the live surface on
      // screen, the same way a refused frame does.
      replay.model.dispose();
      if (closed) emit();
      return;
    }

    preview = { ...target, processor: replay };
    activeSurfaceId = surfaceId;
    emit();
  };

  const reset = () => {
    closePreview();
    processor.model.dispose();
    processor = createProcessor();
    activeSurfaceId = null;
    incomingSurfaceId = null;
    artifacts = [];
    records.clear();
    touched.clear();
    provisional.clear();
    published.clear();
    snapshot = IDLE;
    listeners.forEach((listener) => listener());
  };

  return {
    applyDelivery,
    applyLifecycle,
    noteArtifact,
    showVersion,
    getSurface: (surfaceId) =>
      preview?.surfaceId === surfaceId
        ? preview.processor.model.getSurface(surfaceId)
        : processor.model.getSurface(surfaceId),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => snapshot,
    reset
  };
}
